import type { components } from '@shared/api'

export type Program = components['schemas']['Program']
export type ProgramMutation = components['schemas']['ProgramMutation']
export type ProgramPage = components['schemas']['ProgramPage']
export type ProgramWeek = components['schemas']['ProgramWeek']
export type ProgramDay = components['schemas']['ProgramDay']
export type WorkoutExercise = components['schemas']['WorkoutExercise']
export type ProgramValidationIssue = components['schemas']['ValidationIssue']
export type ProgramValidationReport = components['schemas']['ValidationReport']

export type ProgramPublicationChecklistItem = {
	id: 'saved' | 'structure' | 'training' | 'server' | 'cover'
	label: string
	description: string
	complete: boolean
	required: boolean
	path?: string
}

export type ProgramBuilderExercise = WorkoutExercise & {
	client_id: string
}

export type ProgramBuilderDay = Omit<ProgramDay, 'exercises'> & {
	client_id: string
	exercises: ProgramBuilderExercise[]
}

export type ProgramBuilderWeek = Omit<ProgramWeek, 'days'> & {
	client_id: string
	days: ProgramBuilderDay[]
}

export type ProgramBuilderState = Omit<ProgramMutation, 'weeks'> & {
	weeks: ProgramBuilderWeek[]
}

export type ProgramBuilderIssue = {
	path: string
	message: string
}

export const programKeys = {
	all: ['admin', 'programs'] as const,
	list: (filters: Record<string, unknown>) => [...programKeys.all, 'list', filters] as const,
	detail: (id: string) => [...programKeys.all, 'detail', id] as const,
	cover: (id: string) => [...programKeys.all, 'cover', id] as const,
}

export const programLifecycleLabel: Record<Program['lifecycle'], string> = {
	DRAFT: 'Черновик',
	PUBLISHED: 'Опубликована',
	ARCHIVED: 'В архиве',
}

export const programLifecycleClass: Record<Program['lifecycle'], string> = {
	DRAFT: 'bg-slate-100 text-slate-700',
	PUBLISHED: 'bg-emerald-100 text-emerald-800',
	ARCHIVED: 'bg-amber-100 text-amber-800',
}

export const getProgramStructure = (program: Pick<Program, 'weeks'>) => {
	const days = program.weeks.flatMap(week => week.days)
	return {
		weeks: program.weeks.length,
		days: days.length,
		trainingDays: days.filter(day => !day.is_rest_day).length,
		restDays: days.filter(day => day.is_rest_day).length,
		exercises: days.reduce((total, day) => total + (day.exercises?.length ?? 0), 0),
	}
}

export const createInitialProgramMutation = (name: string, description = ''): ProgramMutation => ({
	name: name.trim(),
	description: description.trim() || undefined,
	weeks: [
		{
			week_number: 1,
			name: 'Неделя 1',
			days: [
				{
					day_number: 1,
					name: 'День отдыха',
					is_rest_day: true,
				},
			],
		},
	],
})

const defaultClientId = () => crypto.randomUUID()

const normalizeNonNegative = (value: number | undefined) =>
	value === undefined || Number.isNaN(value) ? undefined : Math.max(0, Math.trunc(value))

export const createBuilderExercise = (
	exerciseId: string,
	defaults: Partial<WorkoutExercise> = {},
	createId: () => string = defaultClientId,
): ProgramBuilderExercise => ({
	client_id: createId(),
	exercise_id: exerciseId,
	order: 0,
	sets: normalizeNonNegative(defaults.sets),
	repetitions: normalizeNonNegative(defaults.repetitions),
	duration_seconds: normalizeNonNegative(defaults.duration_seconds),
	rest_seconds: normalizeNonNegative(defaults.rest_seconds),
	intensity: defaults.intensity?.trim() || undefined,
	notes: defaults.notes?.trim() || undefined,
})

export const createBuilderDay = (
	isRestDay = true,
	createId: () => string = defaultClientId,
): ProgramBuilderDay => ({
	client_id: createId(),
	day_number: 1,
	name: '',
	is_rest_day: isRestDay,
	exercises: [],
})

export const createBuilderWeek = (
	createId: () => string = defaultClientId,
): ProgramBuilderWeek => ({
	client_id: createId(),
	week_number: 1,
	name: '',
	days: [createBuilderDay(true, createId)],
})

export const programToBuilderState = (
	program: Program,
	createId: () => string = defaultClientId,
): ProgramBuilderState => ({
	name: program.name,
	description: program.description ?? '',
	cover_asset_id: program.cover_asset_id,
	weeks: [...program.weeks]
		.sort((left, right) => left.week_number - right.week_number)
		.map(week => ({
			client_id: createId(),
			week_number: week.week_number,
			name: week.name ?? '',
			days: [...week.days]
				.sort((left, right) => left.day_number - right.day_number)
				.map(day => ({
					client_id: createId(),
					day_number: day.day_number,
					name: day.name ?? '',
					is_rest_day: day.is_rest_day,
					workout_template_id: day.workout_template_id,
					exercises: [...(day.exercises ?? [])]
						.sort((left, right) => left.order - right.order)
						.map(exercise => ({ ...exercise, client_id: createId() })),
				})),
		})),
})

export const normalizeProgramBuilder = (state: ProgramBuilderState): ProgramBuilderState => ({
	name: state.name,
	description: state.description ?? '',
	cover_asset_id: state.cover_asset_id,
	weeks: state.weeks.map((week, weekIndex) => ({
		...week,
		week_number: weekIndex + 1,
		name: week.name ?? '',
		days: week.days.map((day, dayIndex) => ({
			...day,
			day_number: dayIndex + 1,
			name: day.name ?? '',
			workout_template_id: day.is_rest_day ? undefined : day.workout_template_id,
			exercises: day.is_rest_day
				? []
				: day.exercises.map((exercise, exerciseIndex) => ({
					...exercise,
					order: exerciseIndex + 1,
				})),
		})),
	})),
})

export const builderStateToMutation = (state: ProgramBuilderState): ProgramMutation => {
	const normalized = normalizeProgramBuilder(state)
	return {
		name: normalized.name.trim(),
		description: normalized.description?.trim() || undefined,
		cover_asset_id: normalized.cover_asset_id || undefined,
		weeks: normalized.weeks.map(week => ({
			week_number: week.week_number,
			name: week.name?.trim() || undefined,
			days: week.days.map(day => {
				if (day.is_rest_day) {
					return {
						day_number: day.day_number,
						name: day.name?.trim() || undefined,
						is_rest_day: true,
					}
				}
				return {
					day_number: day.day_number,
					name: day.name?.trim() || undefined,
					is_rest_day: false,
					exercises: day.exercises.map((exercise, exerciseIndex) => ({
						exercise_id: exercise.exercise_id,
						order: exerciseIndex + 1,
						sets: normalizeNonNegative(exercise.sets),
						repetitions: normalizeNonNegative(exercise.repetitions),
						duration_seconds: normalizeNonNegative(exercise.duration_seconds),
						rest_seconds: normalizeNonNegative(exercise.rest_seconds),
						intensity: exercise.intensity?.trim() || undefined,
						notes: exercise.notes?.trim() || undefined,
					})),
				}
			}),
		})),
	}
}

export const validateProgramBuilder = (state: ProgramBuilderState): ProgramBuilderIssue[] => {
	const issues: ProgramBuilderIssue[] = []
	if (!state.name.trim()) issues.push({ path: 'name', message: 'Укажите название программы.' })
	if (state.name.trim().length > 240) issues.push({ path: 'name', message: 'Название не должно превышать 240 символов.' })
	if ((state.description ?? '').length > 20_000) issues.push({ path: 'description', message: 'Описание не должно превышать 20 000 символов.' })
	if (!state.weeks.length) issues.push({ path: 'weeks', message: 'Добавьте хотя бы одну неделю.' })
	if (state.weeks.length > 104) issues.push({ path: 'weeks', message: 'Программа не может содержать больше 104 недель.' })
	state.weeks.forEach((week, weekIndex) => {
		if ((week.name ?? '').trim().length > 240) issues.push({ path: `weeks[${weekIndex}].name`, message: `Название недели ${weekIndex + 1} не должно превышать 240 символов.` })
		if (!week.days.length) issues.push({ path: `weeks[${weekIndex}].days`, message: `В неделе ${weekIndex + 1} должен быть хотя бы один день.` })
		if (week.days.length > 7) issues.push({ path: `weeks[${weekIndex}].days`, message: `Неделя ${weekIndex + 1} не может содержать больше 7 дней.` })
		week.days.forEach((day, dayIndex) => {
			const seenExercises = new Set<string>()
			day.exercises.forEach((exercise, exerciseIndex) => {
				if (seenExercises.has(exercise.exercise_id)) {
					issues.push({
						path: `weeks[${weekIndex}].days[${dayIndex}].exercises[${exerciseIndex}].exercise_id`,
						message: `Упражнение не может повторяться в дне ${dayIndex + 1} недели ${weekIndex + 1}.`,
					})
				}
				seenExercises.add(exercise.exercise_id)
			})
			if (!day.is_rest_day && day.exercises.length === 0) {
				issues.push({ path: `weeks[${weekIndex}].days[${dayIndex}].exercises`, message: `Добавьте упражнение в тренировочный день ${dayIndex + 1} недели ${weekIndex + 1}.` })
			}
		})
	})
	return issues
}

export const getProgramPublicationChecklist = (
	state: ProgramBuilderState,
	options: { dirty: boolean; serverValidation: ProgramValidationReport | null },
): ProgramPublicationChecklistItem[] => {
	const normalized = normalizeProgramBuilder(state)
	const localIssues = validateProgramBuilder(normalized)
	const trainingDays = normalized.weeks.flatMap(week => week.days).filter(day => !day.is_rest_day)
	return [
		{
			id: 'saved',
			label: 'Черновик сохранён',
			description: 'Изменение статуса выполняется только для актуальной серверной версии.',
			complete: !options.dirty,
			required: true,
			path: 'version',
		},
		{
			id: 'structure',
			label: 'Структура программы корректна',
			description: 'Название, недели, дни и упражнения проходят локальную проверку.',
			complete: localIssues.length === 0,
			required: true,
			path: localIssues[0]?.path,
		},
		{
			id: 'training',
			label: 'Есть тренировочный день',
			description: 'Опубликованная программа должна содержать минимум одну тренировку.',
			complete: trainingDays.length > 0 && trainingDays.every(day => day.exercises.length > 0),
			required: true,
			path: 'days',
		},
		{
			id: 'server',
			label: 'Серверная проверка пройдена',
			description: 'Проверены сохранённые шаблоны тренировок и каноническая структура программы.',
			complete: options.serverValidation?.valid === true,
			required: true,
			path: options.serverValidation?.issues[0]?.path,
		},
		{
			id: 'cover',
			label: 'Обложка выбрана',
			description: 'Рекомендуется для каталога, но не блокирует публикацию.',
			complete: Boolean(normalized.cover_asset_id),
			required: false,
			path: 'cover_asset_id',
		},
	]
}

export const programValidationPathLabel = (path?: string) => {
	if (!path) return 'Общая проверка'
	if (path === 'name') return 'Название программы'
	if (path === 'description') return 'Описание программы'
	if (path === 'cover_asset_id' || path.startsWith('media')) return 'Медиа программы'
	if (path === 'days') return 'Структура дней'
	const dayMatch = path.match(/^weeks\[(\d+)]\.days\[(\d+)](?:\.(.*))?$/)
	if (dayMatch) return `Неделя ${Number(dayMatch[1]) + 1}, день ${Number(dayMatch[2]) + 1}${dayMatch[3] ? ` · ${dayMatch[3]}` : ''}`
	const weekMatch = path.match(/^weeks\[(\d+)](?:\.(.*))?$/)
	if (weekMatch) return `Неделя ${Number(weekMatch[1]) + 1}${weekMatch[2] ? ` · ${weekMatch[2]}` : ''}`
	const flatDayMatch = path.match(/^days\[(\d+)](?:\.(.*))?$/)
	if (flatDayMatch) return `День программы ${Number(flatDayMatch[1]) + 1}${flatDayMatch[2] ? ` · ${flatDayMatch[2]}` : ''}`
	return path
}

export const workoutExerciseDuration = (exercise: WorkoutExercise) => {
	const sets = Math.max(1, normalizeNonNegative(exercise.sets) ?? 1)
	const work = normalizeNonNegative(exercise.duration_seconds) ?? 0
	const rest = normalizeNonNegative(exercise.rest_seconds) ?? 0
	return work * sets + rest * Math.max(sets - 1, 0)
}

export const programBuilderDuration = (state: ProgramBuilderState) =>
	state.weeks.reduce(
		(programTotal, week) => programTotal + week.days.reduce(
			(weekTotal, day) => weekTotal + day.exercises.reduce(
				(dayTotal, exercise) => dayTotal + workoutExerciseDuration(exercise),
				0,
			),
			0,
		),
		0,
	)

export const duplicateBuilderWeek = (
	week: ProgramBuilderWeek,
	createId: () => string = defaultClientId,
): ProgramBuilderWeek => ({
	...week,
	client_id: createId(),
	days: week.days.map(day => ({
		...day,
		client_id: createId(),
		exercises: day.exercises.map(exercise => ({ ...exercise, client_id: createId() })),
	})),
})

export const duplicateBuilderDay = (
	day: ProgramBuilderDay,
	createId: () => string = defaultClientId,
): ProgramBuilderDay => ({
	...day,
	client_id: createId(),
	exercises: day.exercises.map(exercise => ({ ...exercise, client_id: createId() })),
})

export const moveArrayItem = <T,>(items: readonly T[], from: number, to: number): T[] => {
	if (from < 0 || to < 0 || from >= items.length || to >= items.length || from === to) return [...items]
	const next = [...items]
	const [item] = next.splice(from, 1)
	next.splice(to, 0, item)
	return next
}


const comparableWorkout = (day: ProgramBuilderDay) => ({
	is_rest_day: day.is_rest_day,
	exercises: day.is_rest_day
		? []
		: day.exercises.map((exercise, index) => ({
			exercise_id: exercise.exercise_id,
			order: index,
			sets: normalizeNonNegative(exercise.sets),
			repetitions: normalizeNonNegative(exercise.repetitions),
			duration_seconds: normalizeNonNegative(exercise.duration_seconds),
			rest_seconds: normalizeNonNegative(exercise.rest_seconds),
			intensity: exercise.intensity?.trim() || undefined,
			notes: exercise.notes?.trim() || undefined,
		})),
})

const comparableDay = (day: ProgramBuilderDay) => ({
	name: day.name?.trim() || undefined,
	...comparableWorkout(day),
})

const comparableStructure = (state: ProgramBuilderState) =>
	normalizeProgramBuilder(state).weeks.map(week => ({
		name: week.name?.trim() || undefined,
		days: week.days.map(comparableDay),
	}))

export const builderStateToPatch = (
	state: ProgramBuilderState,
	baseline: ProgramBuilderState,
): ProgramMutation => {
	const current = normalizeProgramBuilder(state)
	const previous = normalizeProgramBuilder(baseline)
	return {
		name: current.name.trim(),
		description: current.description?.trim() ?? '',
		cover_asset_id: current.cover_asset_id || undefined,
		weeks: current.weeks.map(week => ({
			week_number: week.week_number,
			name: week.name?.trim() || undefined,
			days: week.days.map(day => {
				if (day.is_rest_day) {
					return {
						day_number: day.day_number,
						name: day.name?.trim() || undefined,
						is_rest_day: true,
					}
				}
				const previousDay = previous.weeks
					.flatMap(previousWeek => previousWeek.days)
					.find(candidate => candidate.client_id === day.client_id)
				if (
					previousDay &&
					day.workout_template_id &&
					JSON.stringify(comparableWorkout(day)) === JSON.stringify(comparableWorkout(previousDay))
				) {
					return {
						day_number: day.day_number,
						name: day.name?.trim() || undefined,
						is_rest_day: false,
						workout_template_id: day.workout_template_id,
					}
				}
				return {
					day_number: day.day_number,
					name: day.name?.trim() || undefined,
					is_rest_day: false,
					exercises: day.exercises.map((exercise, exerciseIndex) => ({
						exercise_id: exercise.exercise_id,
						order: exerciseIndex + 1,
						sets: normalizeNonNegative(exercise.sets),
						repetitions: normalizeNonNegative(exercise.repetitions),
						duration_seconds: normalizeNonNegative(exercise.duration_seconds),
						rest_seconds: normalizeNonNegative(exercise.rest_seconds),
						intensity: exercise.intensity?.trim() || undefined,
						notes: exercise.notes?.trim() || undefined,
					})),
				}
			}),
		})),
	}
}

export const programBuilderFingerprint = (state: ProgramBuilderState) =>
	JSON.stringify(builderStateToMutation(state))
